
const TIMEOUT_MS = 8000;

const qs = (o) => Object.entries(o).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');

const CYR = /[А-Яа-яЁё]/;
const norm = (t) => String(t).toLowerCase().replace(/ё/g, 'е').replace(/[^a-zа-я0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

// «Михаил Афанасьевич Булгаков» → «Михаил Булгаков», «Александр С. Пушкин» → «Александр Пушкин»
export function shortAuthor(name) {
  const w = String(name).replace(/\u0301/g, '').replace(/\s+/g, ' ').trim().split(' ');
  return w.length > 2 ? `${w[0]} ${w[w.length - 1]}` : w.join(' ');
}

// Порядок выдачи Open Library для русских запросов шумный: чужие издания, оцифровки, диссертации.
// Выше — русское название, обложка и совпадение с запросом.
export function rankBooks(items, query) {
  const q = norm(query);
  const score = (it) => {
    const t = norm(it.title);
    let s = 0;
    if (CYR.test(it.title)) s += 4;
    if (it.sub && CYR.test(it.sub)) s += 1;
    if (it.imgUrl) s += 3;
    // Число изданий в Open Library: у «канонических» книг оно большое, у оцифровок и диссертаций ~1
    if (it.eds) s += Math.min(3, Math.log2(1 + it.eds) / 2.5);
    if (t === q) s += 3;
    else if (q && (t.startsWith(q) || q.startsWith(t))) s += 2;
    else if (q && t.includes(q)) s += 1;
    return s;
  };
  // «Реконизм» на запрос «Война и мир» — шум нечёткого поиска: хотя бы половина слов запроса
  // (по первым пяти буквам) должна встретиться в названии или авторе.
  const tokens = q.split(' ').filter((w) => w.length > 1);
  const related = (it) => {
    if (!tokens.length) return true;
    const hay = norm(it.title + ' ' + it.sub);
    return tokens.filter((w) => hay.includes(w.slice(0, 5))).length >= Math.ceil(tokens.length / 2);
  };
  return items.filter(related).map((it, i) => ({ it, i, s: score(it) })).sort((a, b) => b.s - a.s || a.i - b.i).map((x) => x.it);
}

// «Достоевский, Фёдор Михайлович» → «Фёдор Достоевский»
export function plainName(title) {
  const t = String(title).replace(/\s*\([^)]*\)\s*$/, '');
  const m = /^([^,]+),\s*(\S+)/.exec(t);
  return m ? `${m[2]} ${m[1].trim()}` : t;
}

const PERSON_RE = /писател|поэт|прозаик|драматург|романист|литератор|эссеист|публицист|философ|переводчик|\(\d{3,4}\s*[—–-]/i;

// Search: поиск книг (Apple Books, Open Library) и людей (русская Википедия, Викиданные, Викисклад).
// Всё, что ходит в сеть и хранит состояние (кеш, счётчик запросов Apple), живёт внутри createSearch и получает
// transport снаружи: seam проходит по fetch. Adapters: браузерный fetch (приложение), Node-fetch с User-Agent
// (генераторы каталога), подставной fetch (юнит-тесты).
//
// Interface:
//   createSearch({ fetch, now?, timeoutMs? }) → { searchBooks(q, signal?), searchPeople(q, signal?), bookEditions(title, author, signal?),
//                                     clearSearchCache(), appleBudgetLeft() }
//     fetch: (url, options) → Promise<{ ok, json() }>   — как стандартный fetch
//     now:   () → миллисекунды (для кеша и лимита Apple; по умолчанию Date.now)
//     timeoutMs: ожидание ответа одного источника (по умолчанию 8000)
//   Чистые функции: shortAuthor, rankBooks, plainName.
// Ошибки сети — Error с понятным текстом; в выдаче поле notes говорит, какой источник не ответил.
// Инварианты: ответы с notes не кешируются; Apple не получает больше 15 запросов в минуту.
export function createSearch({ fetch: doFetch, now = () => Date.now(), timeoutMs = TIMEOUT_MS }) {
async function getJson(url, signal) {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs);
  const onAbort = () => ctl.abort();
  if (signal) signal.addEventListener('abort', onAbort, { once: true });
  try {
    const r = await doFetch(url, { signal: ctl.signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
    if (!r.ok) throw new Error('Сервис поиска не ответил. Можно ввести название вручную.');
    return await r.json();
  } catch (e) {
    if (signal && signal.aborted) throw e;
    if (e && e.name === 'AbortError') throw new Error('Поиск отвечает слишком долго. Можно ввести название вручную.');
    throw e instanceof Error && e.message.startsWith('Сервис') ? e : new Error('Не удалось выполнить поиск. Можно ввести название вручную.');
  } finally {
    clearTimeout(t);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

// Кеш ответов на время сессии: повторный запрос (стёрли и набрали снова, переключили формат)
// не уходит в сеть, а у Apple лимит около 20 запросов в минуту. Ответы с пометкой о
// недоступном источнике не кешируем: через минуту источник может снова ответить.
const cache = new Map();
const CACHE_TTL_MS = 10 * 60 * 1000;
function cached(key, fn) {
  const hit = cache.get(key);
  if (hit && now() - hit.t < CACHE_TTL_MS) return Promise.resolve(hit.v);
  return fn().then((v) => {
    if (!(v.notes && v.notes.length)) {
      cache.set(key, { t: now(), v });
      if (cache.size > 60) cache.delete(cache.keys().next().value);
    }
    return v;
  });
}
const clearSearchCache = () => cache.clear();

async function openLibrary(q, signal) {
  const base = { limit: 30, lang: 'rus', fields: 'title,author_name,cover_i,edition_count' };
  const get = (extra) => getJson('https://openlibrary.org/search.json?' + qs({ ...base, ...extra }), signal);
  // Общий запрос и отдельно по полю «название»: второй реже уходит в шум про авторов и диссертации.
  const rs = await Promise.allSettled([get({ q }), get({ title: q })]);
  if (rs.every((r) => r.status === 'rejected')) throw rs[0].reason;
  const out = [];
  const seen = new Set();
  for (const r of rs) {
    if (r.status !== 'fulfilled') continue;
    for (const d of Array.isArray(r.value.docs) ? r.value.docs : []) {
      const title = String(d.title || '').replace(/^["«`]+|["»`]+$/g, '').trim();
      if (!title || title.length > 70) continue;
      const sub = Array.isArray(d.author_name) && d.author_name[0] ? shortAuthor(d.author_name[0]) : '';
      const cover = d.cover_i ? `https://covers.openlibrary.org/b/id/${Number(d.cover_i)}` : '';
      const key = `${norm(title)}|${norm(sub)}|${cover}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        title, sub, source: 'Open Library', eds: Number(d.edition_count) || 0,
        imgUrl: cover ? cover + '-M.jpg' : '', thumbUrl: cover ? cover + '-M.jpg' : '',
      });
    }
  }
  return out;
}

// Apple Books (iTunes Search API): без ключа, русская классика находится при country=us.
// Лимит Apple — около 20 запросов в минуту с IP, поэтому после 15 за минуту пропускаем.
const appleCalls = [];
function appleBudgetLeft(t = now()) {
  while (appleCalls.length && t - appleCalls[0] > 60000) appleCalls.shift();
  return appleCalls.length < 15;
}

async function appleBooks(q, signal) {
  if (!appleBudgetLeft()) throw new Error('лимит Apple');
  appleCalls.push(now());
  const url = 'https://itunes.apple.com/search?' + qs({ term: q, media: 'ebook', country: 'us', limit: 25 });
  const data = await getJson(url, signal);
  const out = [];
  for (const d of Array.isArray(data.results) ? data.results : []) {
    const title = String(d.trackName || '').trim();
    const art = String(d.artworkUrl100 || '');
    if (!title || title.length > 70) continue;
    const ok = /^https:\/\/[^/]+\.mzstatic\.com\//.test(art) && /\/100x100bb\.(jpg|png)$/.test(art);
    out.push({
      title,
      sub: d.artistName ? shortAuthor(String(d.artistName)) : '',
      source: 'Apple Books',
      imgUrl: ok ? art.replace('/100x100bb.', '/400x600bb.') : '',
      thumbUrl: ok ? art.replace('/100x100bb.', '/72x108bb.') : '',
    });
  }
  return out;
}

// → [{ title, sub, source, imgUrl, thumbUrl }] с полем notes: что не ответило. Оба источника
// параллельно; ошибка — только если не ответил ни один. Одинаковые книги склеиваем, оставляя
// вариант с обложкой.
// Сырая выдача обоих источников без склейки (варианты обложек нужны «Другой обложке»).
function fetchBooks(q, signal) {
  return cached('b:' + norm(q), async () => {
    const results = await Promise.allSettled([openLibrary(q, signal), appleBooks(q, signal)]);
    if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
    const [ol, ap] = results;
    const apBudget = ap.status === 'rejected' && ap.reason.message === 'лимит Apple';
    if (results.every((r) => r.status === 'rejected' && !(r === ap && apBudget))) {
      throw (ol.status === 'rejected' ? ol.reason : ap.reason);
    }
    const notes = [];
    if (ol.status === 'rejected') notes.push('Open Library не ответил, показан только Apple Books.');
    if (ap.status === 'rejected') {
      notes.push(apBudget
        ? 'Apple Books пропущен: слишком много запросов за минуту, показан только Open Library. Через минуту поиск снова пойдёт по двум источникам.'
        : 'Apple Books не ответил, показан только Open Library.');
    }
    return { all: results.filter((r) => r.status === 'fulfilled').flatMap((r) => r.value), notes };
  });
}

async function searchBooks(q, signal) {
  const { all, notes } = await fetchBooks(q, signal);
  const seen = new Map();
  for (const it of all) {
    const key = norm(it.title + ' ' + it.sub);
    const prev = seen.get(key);
    if (!prev || (!prev.imgUrl && it.imgUrl)) seen.set(key, it);
  }
  const list = rankBooks([...seen.values()], q).slice(0, 12);
  list.notes = notes;
  return list;
}

// Варианты обложек одной книги (разные издания) — для кнопки «Другая обложка».
async function bookEditions(title, author, signal) {
  const last = String(author || '').trim().split(' ').pop();
  const { all } = await fetchBooks(`${title} ${last}`.trim(), signal);
  const key = norm(title).split(' ').find((w) => w.length >= 4) || norm(title).slice(0, 4);
  const a = norm(last).slice(0, 5);
  // сначала Apple (обложки русских изданий), затем Open Library; повторы картинок убираем
  const ordered = [...all.filter((r) => r.source === 'Apple Books'), ...all.filter((r) => r.source !== 'Apple Books')];
  const seen = new Set();
  return ordered.filter((r) => {
    if (!r.imgUrl || seen.has(r.imgUrl)) return false;
    if (!norm(r.title).includes(key)) return false;
    if (a && !norm(r.sub).includes(a) && !/[a-z]/i.test(last)) return false;
    seen.add(r.imgUrl);
    return true;
  }).slice(0, 14);
}


async function wikiPeople(q, signal) {
  const url = 'https://ru.wikipedia.org/w/api.php?' + qs({
    action: 'query', format: 'json', origin: '*', generator: 'search', gsrsearch: q, gsrlimit: 10,
    prop: 'pageimages|description|pageprops', piprop: 'thumbnail', pithumbsize: 480, pilimit: 10, ppprop: 'wikibase_item',
  });
  const data = await getJson(url, signal);
  const pages = data && data.query && data.query.pages ? Object.values(data.query.pages) : [];
  return pages
    .sort((a, b) => (a.index || 0) - (b.index || 0))
    .filter((p) => p.title && !/\(значения\)/.test(p.title))
    .map((p) => {
      const th = p.thumbnail && /^https:\/\//.test(p.thumbnail.source) ? p.thumbnail.source : '';
      return {
        title: plainName(p.title), sub: '', note: p.description ? String(p.description) : '', source: 'Википедия',
        qid: p.pageprops && p.pageprops.wikibase_item ? p.pageprops.wikibase_item : '', imgUrl: th, thumbUrl: th,
      };
    });
}

// Опечатка («Хемингвей»): Википедия сама предлагает исправление.
async function wikiSuggestion(q, signal) {
  const url = 'https://ru.wikipedia.org/w/api.php?' + qs({
    action: 'query', format: 'json', origin: '*', list: 'search', srsearch: q, srinfo: 'suggestion', srlimit: 1,
  });
  const data = await getJson(url, signal);
  const sug = data && data.query && data.query.searchinfo && data.query.searchinfo.suggestion;
  return sug && norm(sug) !== norm(q) ? String(sug) : '';
}

// Запасной портрет: Викиданные (свойство «изображение», P18) → Викисклад. Свободные снимки,
// которых нет в самой статье. Для нескольких первых людей без картинки.
async function wikidataPortraits(items, signal) {
  const need = items.filter((it) => !it.imgUrl && it.qid && PERSON_RE.test(it.note)).slice(0, 4);
  if (!need.length) return;
  const files = await Promise.all(need.map(async (it) => {
    try {
      const d = await getJson('https://www.wikidata.org/w/api.php?' + qs({
        action: 'wbgetclaims', format: 'json', origin: '*', entity: it.qid, property: 'P18',
      }), signal);
      const c = d && d.claims && d.claims.P18;
      return c && c[0] && c[0].mainsnak && c[0].mainsnak.datavalue ? String(c[0].mainsnak.datavalue.value) : '';
    } catch { return ''; }
  }));
  const titles = files.filter(Boolean).map((f) => 'File:' + f);
  if (!titles.length) return;
  try {
    const d = await getJson('https://commons.wikimedia.org/w/api.php?' + qs({
      action: 'query', format: 'json', origin: '*', titles: titles.join('|'), prop: 'imageinfo', iiprop: 'url', iiurlwidth: 480,
    }), signal);
    const pages = d && d.query && d.query.pages ? Object.values(d.query.pages) : [];
    const byTitle = new Map(pages.map((p) => [String(p.title).replace(/^File:/, '').replace(/_/g, ' '), p.imageinfo && p.imageinfo[0] ? p.imageinfo[0].thumburl : '']));
    need.forEach((it, i) => {
      const u = files[i] ? byTitle.get(files[i].replace(/_/g, ' ')) : '';
      if (u && /^https:\/\/upload\.wikimedia\.org\//.test(u)) { it.imgUrl = u; it.thumbUrl = u; it.source = 'Википедия, Викисклад'; }
    });
  } catch { /* запасной путь не обязателен */ }
}

// → [{ title, sub, note, source, imgUrl, thumbUrl }] с полем notes. Люди выше остальных страниц.
function searchPeople(q, signal) {
  return cached('p:' + norm(q), async () => {
    const notes = [];
    let list = await wikiPeople(q, signal);
    if (!list.length) {
      let sug = '';
      try { sug = await wikiSuggestion(q, signal); } catch { /* без подсказки */ }
      if (sug) {
        list = await wikiPeople(sug, signal);
        if (list.length) notes.push(`Показаны результаты по запросу «${sug}».`);
      }
    }
    // Порядок: страницы людей, у которых в заголовке есть слова запроса («Маргарет Этвуд», а не её роман);
    // затем остальные люди; затем всё прочее.
    const qt = norm(q).split(' ').filter((w) => w.length > 1).map((w) => w.slice(0, 5));
    const named = (it) => qt.length > 0 && qt.every((w) => norm(it.title).includes(w));
    const people = list.filter((it) => PERSON_RE.test(it.note));
    // (у части страниц людей нет краткого описания, поэтому совпадение по заголовку важнее «похоже на человека»)
    list = [...list.filter(named), ...people.filter((it) => !named(it)), ...list.filter((it) => !named(it) && !PERSON_RE.test(it.note))];
    await wikidataPortraits(list, signal);
    list.notes = notes;
    return list;
  });
}

  return { searchBooks, searchPeople, bookEditions, clearSearchCache, appleBudgetLeft };
}

// Экземпляр для приложения: браузерный fetch, подхваченный в момент вызова.
const browserSearch = createSearch({ fetch: (...args) => globalThis.fetch(...args) });
export const { searchBooks, searchPeople, bookEditions, clearSearchCache, appleBudgetLeft } = browserSearch;
